// ---------------------------------------------------------------------------
// StatusTimeline — Audit trail of workflow transitions
// ---------------------------------------------------------------------------

import { Clock } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { formatDateTime, statusLabel } from '@/lib/utils';
import type { ApprovalWorkflow } from '@/types/workflow';
import type { TemplateStatus } from '@/types/template';

const DOT_COLORS: Record<string, string> = {
  live: 'bg-green-500',
  tcm_admin_approved: 'bg-green-500',
  pending_client_review: 'bg-purple-400',
  pending_internal_review: 'bg-purple-400',
  revision_requested: 'bg-honey-500',
  draft: 'bg-silver-400',
};

function dotColor(status: TemplateStatus): string {
  return DOT_COLORS[status] ?? 'bg-silver-400';
}

interface StatusTimelineProps {
  entries: ApprovalWorkflow[];
}

export function StatusTimeline({ entries }: StatusTimelineProps) {
  if (entries.length === 0) {
    return (
      <p className="text-sm text-carbon-400 italic">No status changes recorded.</p>
    );
  }

  const sorted = [...entries].sort(
    (a, b) => new Date(b.created_on).getTime() - new Date(a.created_on).getTime(),
  );

  return (
    <div className="space-y-4">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-carbon-700">
        <Clock className="h-4 w-4" /> Status Timeline
      </h3>

      <div className="relative ml-3">
        {/* Vertical line */}
        <div className="absolute left-1.5 top-1 bottom-1 w-px bg-silver-200" />

        <div className="space-y-4">
          {sorted.map((entry) => (
            <div key={entry.workflow_id} className="relative flex gap-3 pl-6">
              {/* Dot */}
              <div
                className={`absolute left-0 top-1.5 h-3 w-3 rounded-full border-2 border-white ${dotColor(entry.to_status)}`}
              />

              <div className="flex-1 min-w-0">
                <div className="flex items-center flex-wrap gap-2">
                  <Badge variant="outline" className="text-[10px]">{entry.role}</Badge>
                  <span className="text-xs text-carbon-800">
                    {statusLabel(entry.from_status)}
                    <span className="mx-1 text-carbon-400">&rarr;</span>
                    {statusLabel(entry.to_status)}
                  </span>
                </div>
                {entry.comment && (
                  <p className="text-xs text-carbon-600 mt-0.5">{entry.comment}</p>
                )}
                <p className="text-[10px] text-carbon-400 mt-0.5">
                  {formatDateTime(entry.created_on)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
